import { createIsomorphicFn } from '@tanstack/react-start';
import { getRequest } from '@tanstack/react-start/server';

const getBaseUrl = createIsomorphicFn()
	.server(() => {
		const request = getRequest();
		const requestUrl = new URL(request.url);
		const forwardedHost = request.headers.get('x-forwarded-host');
		const forwardedProto = request.headers.get('x-forwarded-proto');

		if (forwardedHost) {
			return `${forwardedProto ?? requestUrl.protocol.replace(':', '')}://${forwardedHost}`;
		}

		return requestUrl.origin;
	})
	.client(() => {
		return window.location.origin;
	});

// INFO: use createUrl for generating url without origin
export function getUrl(
	path = '',
	search?: Record<string, any>,
	opts: Partial<{ noOrigin: boolean }> = {},
) {
	const url = new URL(path, getBaseUrl());

	if (search) {
		for (const [key, val] of Object.entries(search)) {
			if (val === undefined || val === null) {
				url.searchParams.delete(key);
				continue;
			}

			if (typeof val === 'object') {
				url.searchParams.set(key, JSON.stringify(val));
			} else {
				url.searchParams.set(key, String(val));
			}
		}
	}

	if (opts.noOrigin) return url.pathname + url.search;

	return url.toString();
}

const ENV_MAP = [
	{ pattern: /sohan-fi/, env: 'fiverr' },
	{ pattern: /sohan-up/, env: 'upwork' },
	// NOTE: specific patterns must always precede broader ones
	{ pattern: /sohan-fi|sohan-up/, env: 'no-contact' },
	{ pattern: /sohan-notes|sohan-writes|sohanwrites/, env: 'notes' },
	{ pattern: /sohancodes|sohanworks|sohanships|sohanbuilds/, env: 'works' },
	{ pattern: /sohanscript|sohanemon/, env: 'production' },
	{ pattern: /oneman|s-preview/, env: 'preview' },
	{ pattern: /localhost/, env: 'development' },
	{ pattern: /localhost|oneman/, env: 'internal' },
] as const;

export type AppEnv = (typeof ENV_MAP)[number]['env'];

const resolveAppEnv = (): AppEnv =>
	ENV_MAP.find((e) => e.pattern.test(getUrl()))?.env ?? 'development';

/**
 * Returns the current app environment when called with no arguments.
 * Returns `true` if the current URL matches the given environment.
 *
 * Broader group envs (e.g. `"no-contact"`) return `true` even when
 * the primary env is a more specific member (e.g. `"fiverr"`).
 *
 * @example
 * // URL: sohan-fi.dev
 * getAppEnv()              // → "fiverr"
 * getAppEnv("fiverr")      // → true
 * getAppEnv("no-contact")  // → true
 * getAppEnv("upwork")      // → false
 */
export function getAppEnv(): AppEnv;
export function getAppEnv(env: AppEnv): boolean;
export function getAppEnv(env?: AppEnv) {
	if (!env) return resolveAppEnv();
	return ENV_MAP.some((e) => e.pattern.test(getUrl()) && e.env === env);
}

export function getBrand(url: string = getUrl()) {
	const hostname = new URL(url).hostname.replace(/^www\./, '');
	const domain = hostname.split('.')[0].toLowerCase(); // domain without TLD

	let words: [string, string] = [domain, ''];
	// check "sohan" first since it's more specific than "se"
	for (const key of ['sohan', 'se']) {
		const i = domain.indexOf(key);
		if (i !== -1) {
			const rest = (domain.slice(0, i) + domain.slice(i + key.length)).replace(
				/[-_]/g,
				'',
			);
			words = [key, rest];
		}
	}

	return { domain, words };
}
